/**
 * <CareLibraryShelf> — drop-in "Free care guides" panel.
 *
 * Fetches the Care Library catalog from `/api/care-library/items` and
 * surfaces up to `limit` items matching the given `audience` + `topics`
 * filter. Renders a horizontal 3-card row with each card showing title,
 * source organization, accreditation/lane pill, and an outbound link.
 *
 * Used on the discipline landing pages (`/for-<discipline>`) as a
 * mid-page conversion lift — it positions CaraLoom as the clinician's
 * allied platform that helps them educate the families they visit. It
 * also improves time-on-page → reservation conversion.
 *
 * Props:
 *   audience  string  caregiver | clinician (default: caregiver)
 *   topics    array   optional list of catalog topic slugs to filter by
 *   limit     int     max items rendered (default: 3)
 *   title     string  section eyebrow line
 *   subtitle  string  one-line description shown under the eyebrow
 *   ctaPath   string  optional "browse all" link (default: /care-library)
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, ExternalLink, ShieldCheck } from 'lucide-react';
import axios from '../../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CareLibraryShelf = ({
  audience = 'caregiver',
  topics = [],
  limit = 3,
  title = 'Free care guides',
  subtitle = 'Share these with the families you visit — all verified, public-domain, no signup required.',
  ctaPath = '/care-library',
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/care-library/items`, {
          params: { audience },
          _silent: true,  // toast noise on this card is overkill if the API blips
        });
        if (cancelled) return;
        const all = r.data?.items || [];
        // Topic filter — if topics are passed, prefer those, then fall back
        // to any remaining items in the same audience to fill the limit.
        let picked = all;
        if (topics.length) {
          const matched = all.filter((it) => topics.includes(it.topic));
          const rest = all.filter((it) => !topics.includes(it.topic));
          picked = [...matched, ...rest];
        }
        setItems(picked.slice(0, limit));
      } catch (_) {
        // soft-fail — the rest of the discipline page still renders fine
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [audience, JSON.stringify(topics), limit]);

  if (!loading && items.length === 0) return null;

  return (
    <section
      className="py-16 md:py-20 bg-cn-bone-50 dark:bg-cn-ink-900 border-y border-cn-border"
      data-testid="care-library-shelf"
    >
      <div className="cn-container">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="cn-eyebrow mb-3 inline-flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" /> {title}
            </div>
            <h2 className="cn-display text-3xl sm:text-4xl max-w-2xl">{subtitle}</h2>
          </div>
          <Link
            to={ctaPath}
            className="text-sm font-semibold text-cn-primary inline-flex items-center gap-1.5 hover:underline"
            data-testid="care-library-shelf-browse-all"
          >
            Browse the full library <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: limit }).map((_, i) => (
              <div
                key={i}
                className="h-44 rounded-3xl bg-white/70 dark:bg-cn-ink-800/70 border border-cn-border animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it) => (
              <a
                key={it.id}
                href={it.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group cn-card p-6 bg-white dark:bg-cn-ink-800 border-cn-border hover:border-cn-primary/40 hover:shadow-lg transition-all flex flex-col"
                data-testid={`care-library-shelf-card-${it.id}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-cn-primary/10 text-cn-primary">
                    {it.topic.replace(/-/g, ' ')}
                  </span>
                  {it.lane === 'A' && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                      <ShieldCheck className="w-2.5 h-2.5" /> Public domain
                    </span>
                  )}
                </div>
                <h3 className="font-display font-bold text-cn-text dark:text-white leading-snug mb-2 group-hover:text-cn-primary transition-colors">
                  {it.title}
                </h3>
                <p className="text-xs text-cn-text-muted mb-4 line-clamp-2">{it.source_org}</p>
                <div className="mt-auto flex items-center gap-1.5 text-sm font-semibold text-cn-primary">
                  Watch <ExternalLink className="w-3.5 h-3.5" />
                </div>
              </a>
            ))}
          </div>
        )}

        <p className="mt-6 text-[11px] text-cn-text-muted max-w-2xl">
          Curated from federal public-domain and accredited sources. CaraLoom is not the
          publisher — credit and accreditation belong to the original organization.
        </p>
      </div>
    </section>
  );
};

export default CareLibraryShelf;
